// SettingsUserNamePane.jsx

import React, { useEffect, useState } from 'react';

// Context
import { useUser } from 'context/UserContext';

// Firebase
import { updateProfile } from 'firebase/auth';

// Utilities
import { isValidUserName } from 'utilities/Validators';

// Services
import { updateNode } from 'services/nodes/NodeWriteService';

// Components
import SettingsModalPaneHeader from './SettingsModalPaneHeader';
import SettingsModalPaneContents from './SettingsModalPaneContents';
import Button from 'components/button/Button';
import TextInput from 'components/textinput/TextInput';
import { auth } from 'firebaseConfig';

/**
 * SettingsUserNamePane
 *
 * Renders an editable field for the current user's display name.
 *
 * @param {object} theme - Active theme tokens for dynamic styling.
 * @param {object} user - Current signed-in user model.
 *
 * @returns {ReactNode} The rendered user name settings pane.
 */
const SettingsUserNamePane = React.memo(({
    theme,
    user,
}) => {

    // ----- CONTEXT ----- //

    // User Context

    const { setUser } = useUser();

    // ----- STATE ----- //

    // Controlled input value.

    const [nameValue, setNameValue] = useState(user?.data_profile?.name || user?.title || user?.displayName || '');

    // Save in-flight state.
    const [isSaving, setIsSaving] = useState(false);

    // Error message state.
    const [errorMessage, setErrorMessage] = useState('');

    // Status message state.
    const [statusMessage, setStatusMessage] = useState('');

    // ----- EFFECTS ----- //

    // Keep the input synced to the latest stored display name.

    useEffect(() => {
        setNameValue(user?.data_profile?.name || user?.title || user?.displayName || '');
    }, [user?.data_profile?.name, user?.displayName, user?.title]);

    // Current persisted display name.
    const persistedName = String(user?.data_profile?.name || user?.title || user?.displayName || '').trim();

    // Whether the value differs from the persisted display name.
    const hasChanges = nameValue.trim() !== persistedName;

    // Whether the entered name satisfies signup constraints.
    const isNameValid = isValidUserName(nameValue);

    // ----- HANDLERS ----- //

    /**
     * Updates controlled input value and clears stale messages.
     *
     * @param {object} event - Native input change event.
     */

    const handleChange = (event) => {
        setNameValue(event.target.value);
        setErrorMessage('');
        setStatusMessage('');
    };

    /**
     * Persists the new user display name via the user node.
     */
    const handleSave = async () => {
        if (isSaving) return;

        const userKey = String(user?.key || user?.userKey || '').trim();
        const trimmed = nameValue.trim();

        if (!userKey) {
            setErrorMessage('Unable to update your name right now.');
            return;
        }

        if (!isValidUserName(trimmed)) {
            setErrorMessage('Name is required.');
            return;
        }

        setErrorMessage('');
        setStatusMessage('Saving...');
        setIsSaving(true);

        try {
            await updateNode(userKey, {
                'data_profile.name': trimmed,
                'user.name': trimmed,
                'user.title': trimmed,
                title: trimmed,
            }, { actor: user });

            if (auth.currentUser && auth.currentUser.uid === userKey) {
                try {
                    await updateProfile(auth.currentUser, { displayName: trimmed });
                } catch (profileError) {
                    console.warn('[SettingsUserNamePane] Failed to sync auth display name:', profileError);
                }
            }

            setUser((previous) => {
                if (!previous || (previous.key !== userKey && previous.userKey !== userKey)) return previous;

                return {
                    ...previous,
                    displayName: trimmed,
                    name: trimmed,
                    title: trimmed,
                    data_profile: {
                        ...(previous.data_profile || {}),
                        name: trimmed,
                    },
                    user: {
                        ...(previous.user || {}),
                        name: trimmed,
                        title: trimmed,
                    },
                };
            });

            setStatusMessage('Name updated.');
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to update your name.');
            setStatusMessage('');
        } finally {
            setIsSaving(false);
        }
    };

    // ----- RENDER ----- //

    return (
        <>
            <SettingsModalPaneHeader
              title="My Name"
              subtitle="Update the display name shown on your account."
            />

            <SettingsModalPaneContents>
                <div
                  className="settings-modal-name-card"
                  style={{
                        borderColor: theme.backgroundHover,
                    }}
                >
                    <TextInput
                      placeholder="Name..."
                      value={nameValue}
                      width="100%"
                      error={Boolean(errorMessage)}
                      helperText={errorMessage}
                      disabled={isSaving}
                      required={true}
                      type="text"
                      onChange={handleChange}
                      onEnterPress={hasChanges && isNameValid ? handleSave : undefined}
                    />

                    <Button
                      iconKey="save"
                      label="Save"
                      enabled={hasChanges && isNameValid && !isSaving}
                      onClick={handleSave}
                    />
                </div>

                {statusMessage && !errorMessage && (
                    <div className="settings-modal-status">{statusMessage}</div>
                )}
            </SettingsModalPaneContents>
        </>
    );
});

// Dev Tools
SettingsUserNamePane.displayName = 'SettingsUserNamePane';

export default SettingsUserNamePane;
